import { useEffect, useState, useCallback } from 'react';
import api from '../services/ApiService';

type Device = {
  id: number;
  extension_number: string;
  display_name: string;
  is_active: number;
  tenant_id?: number;
  registered: boolean;
  pjsip_status: string;
  contact_uri: string | null;
  rtt_ms: number | null;
};

type Stats = { total: number; registered: number; unregistered: number };

const STATUS_COLOR: Record<string, string> = {
  Avail:        '#10b981',
  NonQualify:   '#f59e0b',
  NonQual:      '#f59e0b',
  Unknown:      '#64748b',
  Unavail:      '#ef4444',
  'Not Registered': '#ef4444',
};

function Badge({ status }: { status: string }) {
  const color = STATUS_COLOR[status] || '#64748b';
  const label = status === 'Avail' ? 'Registered' : (status === 'NonQualify' || status === 'NonQual') ? 'No Qualify' : status;
  return (
    <span style={{
      background: `${color}18`, border: `1px solid ${color}40`,
      color, borderRadius: 4, padding: '2px 8px',
      fontSize: 11, fontWeight: 700,
    }}>{label}</span>
  );
}

export default function RegisteredDevicesPage() {
  const [devices, setDevices] = useState<Device[]>([]);
  const [stats, setStats]     = useState<Stats | null>(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter]   = useState<'all' | 'registered' | 'unregistered'>('all');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [error, setError]     = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await (api as any).getRegisteredDevices();
      setDevices(data.devices || []);
      setStats(data.stats || null);
      setLastRefresh(new Date());
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Failed to load device status');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 15_000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const shown = devices.filter(d => {
    if (filter === 'registered')   return d.registered;
    if (filter === 'unregistered') return !d.registered;
    return true;
  });

  return (
    <div style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9' }}>Registered Devices</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 2 }}>
            SIP extension registration status
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13, color: '#94a3b8', cursor: 'pointer' }}>
          <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)}
            style={{ accentColor: '#8b5cf6' }} />
          Auto-refresh (15s)
        </label>
        <button onClick={load} disabled={loading}
          style={{
            background: 'rgba(30,41,59,0.8)', border: '1px solid rgba(100,116,139,0.3)',
            color: '#94a3b8', borderRadius: 8, padding: '8px 14px', fontSize: 13, cursor: 'pointer',
          }}>
          {loading ? '…' : '↻ Refresh'}
        </button>
      </div>

      {error && (
        <div style={{ background: 'rgba(239,68,68,0.12)', border: '1px solid #ef444440', color: '#fca5a5',
          borderRadius: 8, padding: '10px 14px', fontSize: 13, marginBottom: 16 }}>
          {error}
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {[
            { label: 'Total Extensions', value: stats.total, color: '#8b5cf6' },
            { label: 'Registered',       value: stats.registered, color: '#10b981' },
            { label: 'Not Registered',   value: stats.unregistered, color: '#ef4444' },
          ].map(s => (
            <div key={s.label} style={{
              background: 'rgba(30,41,59,0.7)', border: `1px solid ${s.color}25`,
              borderRadius: 12, padding: '14px 20px', flex: 1,
            }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
          <div style={{
            background: 'rgba(30,41,59,0.5)', border: '1px solid rgba(100,116,139,0.15)',
            borderRadius: 12, padding: '14px 20px', flex: 2,
            display: 'flex', flexDirection: 'column', justifyContent: 'center',
          }}>
            <div style={{ fontSize: 12, color: '#64748b' }}>Last refresh</div>
            <div style={{ fontSize: 13, color: '#e2e8f0', fontWeight: 600, marginTop: 2 }}>
              {lastRefresh ? lastRefresh.toLocaleTimeString() : '—'}
            </div>
            <div style={{ marginTop: 6, background: 'rgba(16,185,129,0.2)', borderRadius: 4, height: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%', background: '#10b981',
                width: `${stats.total > 0 ? Math.round((stats.registered / stats.total) * 100) : 0}%`,
                transition: 'width 0.5s',
              }} />
            </div>
            <div style={{ fontSize: 11, color: '#475569', marginTop: 3 }}>
              {stats.total > 0 ? Math.round((stats.registered / stats.total) * 100) : 0}% registered
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14 }}>
        {(['all', 'registered', 'unregistered'] as const).map(f => (
          <button key={f} onClick={() => setFilter(f)} style={{
            background: filter === f ? 'rgba(139,92,246,0.2)' : 'rgba(30,41,59,0.5)',
            border: `1px solid ${filter === f ? '#8b5cf6' : 'rgba(100,116,139,0.2)'}`,
            color: filter === f ? '#c4b5fd' : '#94a3b8',
            borderRadius: 6, padding: '5px 14px', fontSize: 12,
            fontWeight: filter === f ? 700 : 400, cursor: 'pointer', textTransform: 'capitalize',
          }}>
            {f} {f !== 'all' && stats ? `(${f === 'registered' ? stats.registered : stats.unregistered})` : ''}
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={{ background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(100,116,139,0.15)', borderRadius: 12, overflow: 'hidden' }}>
        {shown.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#475569', fontSize: 14 }}>
            {loading ? 'Loading…' : 'No extensions found.'}
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: 'rgba(30,41,59,0.8)', borderBottom: '1px solid rgba(100,116,139,0.15)' }}>
                {['Extension', 'Display Name', 'Status', 'Contact URI', 'RTT (ms)', 'Active'].map(h => (
                  <th key={h} style={{ padding: '10px 14px', textAlign: 'left', color: '#64748b', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shown.map((d, i) => (
                <tr key={d.id} style={{
                  borderBottom: '1px solid rgba(100,116,139,0.08)',
                  background: i % 2 === 0 ? 'transparent' : 'rgba(30,41,59,0.15)',
                }}>
                  <td style={{ padding: '10px 14px', fontWeight: 700, color: '#e2e8f0', fontFamily: 'monospace' }}>
                    {d.extension_number}
                  </td>
                  <td style={{ padding: '10px 14px', color: '#cbd5e1' }}>{d.display_name || '—'}</td>
                  <td style={{ padding: '10px 14px' }}>
                    <Badge status={d.pjsip_status} />
                  </td>
                  <td style={{ padding: '10px 14px', color: '#64748b', fontSize: 11, fontFamily: 'monospace', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.contact_uri || <span style={{ color: '#334155' }}>—</span>}
                  </td>
                  <td style={{ padding: '10px 14px', color: d.rtt_ms ? '#10b981' : '#475569', fontFamily: 'monospace' }}>
                    {d.rtt_ms != null ? `${d.rtt_ms.toFixed(1)} ms` : '—'}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <span style={{ color: d.is_active ? '#10b981' : '#ef4444', fontSize: 11, fontWeight: 700 }}>
                      {d.is_active ? 'Yes' : 'No'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: '#334155' }}>
        {shown.length} extension{shown.length !== 1 ? 's' : ''}
        {filter !== 'all' ? ` · filter: ${filter}` : ''}
      </div>
    </div>
  );
}
